import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { formatFileSize } from '@witmetrics/utils/strings';
import type { Nullish, UndecoratedFileVersion } from '@witmetrics/utils';
import {
  useDispatch,
  useFile,
  useFileVersionFromFileId,
} from '@witmetrics/data-store';
import { COLLECTION_VIEWS, type CollectionView } from '@witmetrics/unison-core';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import { useLoadedFiles } from '@/providers/LoadedFilesProvider';
import {
  fetchFileVersion,
  getFileName,
  getFileTypeLabel,
  openFileDownloadLink,
} from '@/utils/files';
import { showErrorToaster } from '@/utils/toasters';
import Card from '@/components/Card';
import TruncatedText from '@/components/TruncatedText';
import FileCategoryIcon from '@/components/FileCategoryIcon';
import DownloadToaster from '@/toasters/DownloadToaster';
import DeleteFileDialog from '@/dialogs/DeleteFileDialog';
import FileInfoDialog from '@/dialogs/FileInfoDialog';
import ShareFileDialog from '@/dialogs/ShareFileDialog';
import MoveContentDialog from '@/dialogs/MoveContentDialog';
import ContentMenu from '@/menus/ContentMenu';
import { OptionsIcon } from '@/icons';
import ListItem from '../FileCollectionList/ListItem';
import GridItem from '../FileCollectionGrid/GridItem';
import ItemImage from '../ItemImage';
import type { BaseItemProps, CollectionVariant } from '../types';
import { classes, styles } from './utils';

export type FileItemProps = {
  active?: boolean;
  dense?: boolean;
  showDetails?: boolean;
  variant?: CollectionVariant;
  className?: string;
  fileId: number;
  view?: CollectionView;
  onSelect: (fileId: number) => void;
  onEdit?: () => void;
  onDelete?: () => void;
};

export default function FileItem({
  active,
  dense,
  showDetails,
  variant,
  className,
  fileId,
  view = COLLECTION_VIEWS.LIST,
  onSelect,
  onEdit,
  onDelete,
}: FileItemProps) {
  const menuToggleRef = useRef(null);
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const file = useFile(fileId);
  const fileVersion = useFileVersionFromFileId(fileId);
  const { loadedFiles, onLoadPreview } = useLoadedFiles();
  const [isMenuOpen, toggleMenu] = useToggle();
  const [isShareDialogOpen, toggleShareDialog] = useToggle();
  const [isMoveDialogOpen, toggleMoveDialog] = useToggle();
  const [isEditDialogShown, toggleEditDialog] = useToggle();
  const [isDeleteDialogShown, toggleDeleteDialog] = useToggle();
  const [isPreparingDownload, togglePreparingDownload] = useToggle();
  const [isDownloadToasterOpen, toggleDownloadToaster] = useToggle();
  const [isFetchingFileVersion, toggleFetchingFileVersion] = useToggle();

  useEffect(() => {
    if (!fileVersion && !isFetchingFileVersion) handleFetchFileVersion();
    else if (fileVersion) onLoadPreview(fileVersion);
  }, [fileVersion, isFetchingFileVersion]);

  const handleFetchFileVersion = async () => {
    try {
      toggleFetchingFileVersion(true);
      const version = await fetchFileVersion(fileId);
      if (version) dispatch.addFileVersions([version]);
      toggleFetchingFileVersion(false);
    } catch (err) {
      onApiError(err, 'Error fetching file version');
    }
  };

  const handleDownload = async (): Promise<any> => {
    try {
      toggleMenu(false);
      togglePreparingDownload(true);
      toggleDownloadToaster(true);
      let version: UndecoratedFileVersion | Nullish = fileVersion;
      // Might just need to be fetched
      if (!version) version = await fetchFileVersion(file!.id);
      // Nope, doesn't exist
      if (!version) return showErrorToaster('File version not found');
      await openFileDownloadLink(version.id, version.name);
      togglePreparingDownload(false);
    } catch (err) {
      handleCloseDownloadToaster();
      onApiError(err, 'Error downloading file');
    }
  };

  const handleCloseDownloadToaster = () => {
    togglePreparingDownload(false);
    toggleDownloadToaster(false);
  };

  if (!file) return null;

  const isAurora = variant === 'aurora';
  const preview = fileVersion
    ? loadedFiles[`preview-${fileVersion.id}`]
    : undefined;

  const dialogs = (
    <>
      {isShareDialogOpen && (
        <ShareFileDialog
          fileId={fileId}
          onClose={() => toggleShareDialog(false)}
        />
      )}
      {isMoveDialogOpen && (
        <MoveContentDialog
          fileId={fileId}
          onClose={() => toggleMoveDialog(false)}
        />
      )}
      {isEditDialogShown && (
        <FileInfoDialog
          fileId={fileId}
          onSave={onEdit}
          onClose={toggleEditDialog}
        />
      )}
      {isDeleteDialogShown && (
        <DeleteFileDialog
          fileId={fileId}
          onDelete={onDelete}
          onClose={toggleDeleteDialog}
        />
      )}
      <DownloadToaster
        open={isDownloadToasterOpen}
        isLoading={isPreparingDownload}
        message="Preparing file..."
        onClose={handleCloseDownloadToaster}
      />
    </>
  );

  if (showDetails) {
    const filePreview = preview ? (
      <img
        className={isAurora ? classes.auroraPreview : classes.listPreview}
        alt="preview"
        src={preview as string}
      />
    ) : (
      <FileCategoryIcon
        variant={isAurora ? 'text' : undefined}
        size="base"
        file={file}
      />
    );
    const sharedProps: Omit<BaseItemProps, 'icon'> = {
      variant,
      overrideIcon: true,
      name: getFileName(file),
      updatedAt: file.updatedAt,
      details: fileVersion?.size
        ? formatFileSize(parseInt(fileVersion.size))
        : undefined,
      iconVariant: 'text',
      tag: getFileTypeLabel(file.fileTypeId),
      onView: () => onSelect(fileId),
      onDownload: handleDownload,
      onShare: toggleShareDialog,
      onMove: toggleMoveDialog,
      onEdit: toggleEditDialog,
      onDelete: toggleDeleteDialog,
    };

    return (
      <>
        {dialogs}
        {view === COLLECTION_VIEWS.LIST ? (
          <ListItem
            {...sharedProps}
            icon={
              isAurora ? (
                <span className={classes.auroraIcon}>{filePreview}</span>
              ) : (
                filePreview
              )
            }
          />
        ) : (
          <GridItem
            {...sharedProps}
            icon={<FileCategoryIcon size="lg" file={file} />}
            preview={
              preview && (
                <img
                  className={classes.gridPreview}
                  alt="preview"
                  src={preview as string}
                />
              )
            }
          />
        )}
      </>
    );
  }

  return (
    <>
      {dialogs}
      <Card
        sx={styles.card}
        className={twMerge(
          isAurora ? classes.auroraCard : classes.card,
          dense && classes.cardDense,
          active && (isAurora ? classes.auroraActive : classes.active),
          className
        )}
        onClick={() => onSelect(fileId)}>
        <div
          className={twMerge(classes.content, dense && classes.contentDense)}>
          <ItemImage dense={dense} fileId={fileId} />
          <TruncatedText
            className={classes.titleWrapper}
            textClassName={isAurora ? classes.auroraCardTitle : classes.title}>
            {file.name}
          </TruncatedText>
          <div
            className={classes.optionsWrapper}
            onClick={(e) => e.stopPropagation()}>
            <OptionsIcon
              ref={menuToggleRef}
              className={twMerge(
                classes.optionsIcon,
                isMenuOpen && classes.optionsIconOpen
              )}
              onClick={() => toggleMenu()}
            />
            <ContentMenu
              anchorTo={menuToggleRef.current}
              open={isMenuOpen}
              onDownload={handleDownload}
              onShare={toggleShareDialog}
              onMove={toggleMoveDialog}
              onView={() => onSelect(fileId)}
              onEdit={toggleEditDialog}
              onDelete={toggleDeleteDialog}
              onClose={() => toggleMenu(false)}
            />
          </div>
        </div>
      </Card>
    </>
  );
}
